import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, ArrowRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface InsuranceRenewal {
  assetId: string;
  assetCode: string;
  assetName: string;
  renewalDate: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceValue?: number;
}

interface UpcomingInsuranceRenewalsCardProps {
  readonly renewals?: InsuranceRenewal[];
}

export function UpcomingInsuranceRenewalsCard({ renewals = [] }: UpcomingInsuranceRenewalsCardProps): React.ReactElement {
  const navigate = useNavigate();

  // Format the renewal date for display
  const formatRenewalDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'do MMMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Use the renewals data from the API
  const upcomingRenewals = renewals.map(renewal => ({
    id: renewal.assetId,
    code: renewal.assetCode,
    name: renewal.assetName,
    renewalDate: formatRenewalDate(renewal.renewalDate),
    insuranceProvider: renewal.insuranceProvider,
    insuranceValue: renewal.insuranceValue,
  }));

  const totalCount = upcomingRenewals.length;
  const displayedRenewals = upcomingRenewals.slice(0, 5);
  const hasMore = totalCount > 5;

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets?id=${assetId}&tab=supplier`);
  };

  const handleViewMore = () => {
    navigate('/assets?filter=insurance-due');
  };

  return (
    <Card className="relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black">
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Upcoming Insurance Renewals</CardTitle>
            <CardDescription>Top {displayedRenewals.length} assets requiring renewal</CardDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="rounded-full p-1.5 hover:bg-background/80 transition-colors">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Shows assets with insurance expiring within the next 30 days. Click on any asset to view details.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="relative">
        {displayedRenewals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No insurance renewals due in the next 30 days</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Asset ID</TableHead>
                    <TableHead className="font-semibold">Asset Name</TableHead>
                    <TableHead className="font-semibold">Renewal Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedRenewals.map((asset) => (
                    <TableRow
                      key={asset.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleAssetClick(asset.id)}
                    >
                      <TableCell className="font-medium">{asset.code}</TableCell>
                      <TableCell>{asset.name}</TableCell>
                      <TableCell>{asset.renewalDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {hasMore && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleViewMore}
                >
                  View All {totalCount} Renewals
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
